import { BaseService } from './base';
import { Service } from '../models/service';
import { ServiceAssociation } from '../models/serviceAssociation';
import { ServiceForm } from '../models/serviceForm';
import { ServiceResource } from '../models/serviceResource';
import { ServiceAutomation } from '../models/serviceAutomation';

import { ApiRequest } from '../lib/request';
import { ApiResponse, ApiListResponse } from '../lib/response';

export class ServiceService extends BaseService<Service> {
  override model = Service;
  override modelName = 'service';
  override modelLabel = 'Service';

  async filter({
    page = 1,
    limit = 20,
    filters = {}
  }: {
    page?: number;
    limit?: number;
    filters?: Record<string, any>;
  } = {}): Promise<ApiListResponse<Service>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/list`,
      query: { page: page.toString(), limit: limit.toString() },
      data: filters,
    });

    return await this.requestList<Service>(request, this.model);
  }

  async duplicate(service: Service): Promise<ApiResponse<Service>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${service.objectID()}/duplicate`,
    });

    return await this.requestItem<Service>(request, this.model);
  }

  async addForm(
    service: Service,
    serviceForm: ServiceForm
  ): Promise<ApiResponse<Service>> {
    const data = {
      idForm: serviceForm.form?.idForm,
      ...serviceForm.toJSON?.() || serviceForm,
    };

    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${service.objectID()}/addForm`,
      data,
    });

    return await this.requestItem<Service>(request, this.model);
  }

  async updateForm(
    service: Service,
    serviceForm: ServiceForm
  ): Promise<ApiResponse<Service>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${service.objectID()}/updateForm`,
      data: serviceForm.toJSON?.() || serviceForm,
    });

    return await this.requestItem<Service>(request, this.model);
  }

  async removeForm(
    service: Service,
    serviceForm: ServiceForm
  ): Promise<ApiResponse<Service>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${service.objectID()}/removeForm`,
      data: serviceForm.toJSON?.() || serviceForm,
    });

    return await this.requestItem<Service>(request, this.model);
  }

  async addResource(
    service: Service,
    serviceResource: ServiceResource
  ): Promise<ApiResponse<Service>> {
    const data = {
      idEmployee: serviceResource.resource?.idEmployee,
      idResourceType: serviceResource.resourceType?.idResourceType,
      ...serviceResource.toJSON?.() || serviceResource,
    };

    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${service.objectID()}/addResource`,
      data,
    });

    return await this.requestItem<Service>(request, this.model);
  }

  async updateResource(
    service: Service,
    serviceResource: ServiceResource
  ): Promise<ApiResponse<Service>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${service.objectID()}/updateResource`,
      data: serviceResource.toJSON?.() || serviceResource,
    });

    return await this.requestItem<Service>(request, this.model);
  }

  async removeResource(
    service: Service,
    serviceResource: ServiceResource
  ): Promise<ApiResponse<Service>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${service.objectID()}/removeResource`,
      data: serviceResource.toJSON?.() || serviceResource,
    });

    return await this.requestItem<Service>(request, this.model);
  }

  async addAutomation(
    service: Service,
    serviceAutomation: ServiceAutomation
  ): Promise<ApiResponse<Service>> {
    const data = {
      idContentTemplate: serviceAutomation.contentTemplate?.idContentTemplate,
      ...serviceAutomation.toJSON?.() || serviceAutomation,
    };

    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${service.objectID()}/automations`,
      data,
    });

    return await this.requestItem<Service>(request, this.model);
  }

  async updateAutomation(
    service: Service,
    serviceAutomation: ServiceAutomation
  ): Promise<ApiResponse<Service>> {
    const request = new ApiRequest({
      method: 'PUT',
      url: `${this.modelBaseUrl}/${service.objectID()}/automations/${serviceAutomation.idServiceAutomation}`,
      data: serviceAutomation.toJSON?.() || serviceAutomation,
    });

    return await this.requestItem<Service>(request, this.model);
  }

  async removeAutomation(
    service: Service,
    serviceAutomation: ServiceAutomation
  ): Promise<ApiResponse<Service>> {
    const request = new ApiRequest({
      method: 'DELETE',
      url: `${this.modelBaseUrl}/${service.objectID()}/automations/${serviceAutomation.idServiceAutomation}`,
      data: serviceAutomation.toJSON?.() || serviceAutomation,
    });

    return await this.requestItem<Service>(request, this.model);
  }

  // Association (Add-on) methods
  async associations(service: Service): Promise<ApiListResponse<ServiceAssociation>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${service.objectID()}/associations`,
    });

    return await this.requestList<ServiceAssociation>(request, ServiceAssociation);
  }

  async addAssociation(
    service: Service,
    serviceAssociation: ServiceAssociation | Record<string, any>
  ): Promise<ApiResponse<Service>> {
    const {
      target_type,
      target_id,
      type,
      orderType,
      discountType,
      discountAmount,
    } = serviceAssociation;
    const data = {
      target_type,
      target_id,
      type,
      orderType,
      discountType,
      discountAmount: discountAmount ?? 0,
    };

    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${service.objectID()}/associations`,
      data,
    });

    return await this.requestItem<Service>(request, this.model);
  }

  async updateAssociation(
    service: Service,
    serviceAssociation: ServiceAssociation | Record<string, any>
  ): Promise<ApiResponse<Service>> {
    const idAssociation = serviceAssociation instanceof ServiceAssociation
      ? serviceAssociation.idAssociation
      : serviceAssociation.idAssociation;
    const data = serviceAssociation instanceof ServiceAssociation
      ? serviceAssociation.toJSON()
      : serviceAssociation;

    const request = new ApiRequest({
      method: 'PUT',
      url: `${this.modelBaseUrl}/${service.objectID()}/associations/${idAssociation}`,
      data,
    });

    return await this.requestItem<Service>(request, this.model);
  }

  async removeAssociation(
    service: Service,
    serviceAssociation: ServiceAssociation | Record<string, any>
  ): Promise<ApiResponse<Service>> {
    const idAssociation = serviceAssociation instanceof ServiceAssociation
      ? serviceAssociation.idAssociation
      : serviceAssociation.idAssociation;
    const data = serviceAssociation instanceof ServiceAssociation
      ? serviceAssociation.toJSON()
      : serviceAssociation;

    const request = new ApiRequest({
      method: 'DELETE',
      url: `${this.modelBaseUrl}/${service.objectID()}/associations/${idAssociation}`,
      data,
    });

    return await this.requestItem<Service>(request, this.model);
  }

  async addPhoto(
    service: Service,
    image: File,
    onUploadProgress?: (progressEvent: import('axios').AxiosProgressEvent) => void,
  ): Promise<ApiResponse<Service>> {
    const formData = new FormData();
    formData.append('image', image);

    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${service.objectID()}/addPhoto`,
      data: formData,
      onUploadProgress,
    });

    return await this.requestItem<Service>(request, this.model);
  }

  async deletePhoto(service: Service): Promise<ApiResponse<Service>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${service.objectID()}/deletePhoto`,
    });

    return await this.requestItem<Service>(request, this.model);
  }

  async updatePrices(
    service: Service,
    {
      updateEmployees = false,
      updateFutureReservations = false
    }: {
      updateEmployees?: boolean;
      updateFutureReservations?: boolean;
    } = {}
  ): Promise<ApiResponse<any>> {
    const data = {
      updateEmployees,
      updateFutureReservations,
    };

    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${service.objectID()}/updatePrices`,
      data,
    });

    return await this.request(request);
  }

  async categorised(modifiedSince?: Date): Promise<ApiListResponse<any>> {
    const headers: Record<string, string> = {};

    if (modifiedSince) {
      headers['if-modified-since'] = modifiedSince.toUTCString();
    }

    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/categorised`,
      headers,
    });

    // TODO: Create ServiceCategorised model
    return await this.requestList<any>(request, this.model);
  }

  async filterAll({
    filters = {},
    limit = 100,
  }: {
    filters?: Record<string, any>;
    limit?: number;
  } = {}): Promise<Service[]> {
    const items: Service[] = [];
    let currentPage = 1;
    let totalPages = 1;

    do {
      const response = await this.filter({
        page: currentPage,
        limit,
        filters,
      });

      items.push(...response.items);

      if (response.paging?.itemCount && response.paging?.limit) {
        totalPages = Math.ceil(response.paging.itemCount / response.paging.limit);
      }

      currentPage++;
    } while (currentPage <= totalPages);

    return items;
  }
}
